import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money } from "@/components/opportunities/shared";
import { formatDate } from "@/lib/dates";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { CircleNotch, ArrowRight, CaretRight } from "@phosphor-icons/react";

// Batch G: one consistent right-side drawer behind the dashboard cards.
// Read-only — it lists the records already counted by the clicked category and
// links each one to its existing prospect / opportunity screen.

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = formatDate(String(iso).slice(0, 10));
  return d || "—";
};

function LinkName({ to, children, testId }) {
  const navigate = useNavigate();
  if (!to) return <span className="font-semibold text-[#09090B]">{children || "—"}</span>;
  return (
    <button type="button" onClick={() => navigate(to)} data-testid={testId}
      className="truncate text-left font-semibold text-[#002248] hover:text-[#2E86D6] hover:underline">
      {children || "—"}
    </button>
  );
}

function Meta({ label, value }) {
  return (
    <span className="mr-3 inline-block whitespace-nowrap">
      <span className="text-[#9CA3AF]">{label}:</span>{" "}
      <span className="font-medium text-[#52525B]">{value || "—"}</span>
    </span>
  );
}

function RowShell({ children, testId }) {
  return (
    <div className="border-b border-[#F0F1F3] px-4 py-2.5 last:border-0" data-testid={testId}>
      {children}
    </div>
  );
}

function StageRows({ rows }) {
  return rows.map((r) => (
    <RowShell key={r.opportunity_id} testId={`drilldown-row-${r.opportunity_id}`}>
      <div className="flex items-baseline justify-between gap-2">
        <LinkName to={`/prospects/${r.prospect_id}`} testId={`drilldown-org-${r.opportunity_id}`}>{r.organization}</LinkName>
        <span className="shrink-0 text-xs font-bold tabular-nums text-[#002248]">{money(r.value)}</span>
      </div>
      <div className="mt-0.5 flex items-center gap-1 text-xs text-[#52525B]">
        <CaretRight size={11} className="shrink-0 text-[#9CA3AF]" />
        <LinkName to={`/opportunities/${r.opportunity_id}`} testId={`drilldown-opp-${r.opportunity_id}`}>{r.opportunity_name}</LinkName>
      </div>
      <p className="mt-1 text-[11px] leading-relaxed">
        <Meta label="Stage" value={r.stage_name} />
        <Meta label="Salesperson" value={r.salesperson} />
        <Meta label="Last activity" value={fmtWhen(r.last_activity)} />
        <Meta label="Next action" value={r.next_action
          ? `${r.next_action}${r.next_action_due ? ` (${fmtWhen(r.next_action_due)})` : ""}`
          : r.next_action_due ? `Due ${fmtWhen(r.next_action_due)}` : null} />
      </p>
    </RowShell>
  ));
}

function MovementRows({ rows }) {
  return rows.map((r, i) => (
    <RowShell key={`${r.opportunity_id}-${r.moved_at}-${i}`} testId={`drilldown-move-${r.opportunity_id}-${i}`}>
      <div className="flex items-baseline justify-between gap-2">
        <LinkName to={r.prospect_id ? `/prospects/${r.prospect_id}` : null}>{r.organization}</LinkName>
        <span className="shrink-0 text-[11px] text-[#6D6D70]">{fmtWhen(r.moved_at)}</span>
      </div>
      <div className="mt-0.5 flex items-center gap-1 text-xs text-[#52525B]">
        <CaretRight size={11} className="shrink-0 text-[#9CA3AF]" />
        <LinkName to={`/opportunities/${r.opportunity_id}`}>{r.opportunity_name}</LinkName>
      </div>
      <p className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-[#52525B]">
        <span className="rounded-sm bg-[#F4F4F5] px-1.5 py-0.5 font-medium">{r.previous_stage || "New"}</span>
        <ArrowRight size={11} className="text-[#9CA3AF]" />
        <span className="rounded-sm bg-[#002248]/8 px-1.5 py-0.5 font-semibold text-[#002248]">{r.new_stage}</span>
      </p>
      <p className="mt-1 text-[11px] leading-relaxed">
        <Meta label="Salesperson" value={r.salesperson} />
        <Meta label="Recorded by" value={r.moved_by} />
      </p>
    </RowShell>
  ));
}

const ST_STATUS = { active: "Active", completed: "Completed", eligible: "Eligible — not started" };

function SevenTouchRows({ rows }) {
  return rows.map((r) => (
    <RowShell key={r.prospect_id} testId={`drilldown-row-${r.prospect_id}`}>
      <div className="flex items-baseline justify-between gap-2">
        <LinkName to={`/prospects/${r.prospect_id}`} testId={`drilldown-org-${r.prospect_id}`}>{r.organization}</LinkName>
        <span className="shrink-0 text-xs font-bold text-[#002248]">{r.touches}/7 touches</span>
      </div>
      <p className="mt-1 text-[11px] leading-relaxed">
        <Meta label="Salesperson" value={r.salesperson} />
        <Meta label="Most recent touch" value={fmtWhen(r.last_touch)} />
        <Meta label="Next touch due" value={fmtWhen(r.next_due)} />
        <Meta label="Status" value={ST_STATUS[r.status] || r.status} />
      </p>
    </RowShell>
  ));
}

function OutcomeRows({ rows }) {
  return rows.map((r) => (
    <RowShell key={r.activity_id} testId={`drilldown-row-${r.activity_id}`}>
      <div className="flex items-baseline justify-between gap-2">
        <LinkName to={`/prospects/${r.prospect_id}`} testId={`drilldown-org-${r.activity_id}`}>{r.organization}</LinkName>
        <span className="shrink-0 text-[11px] text-[#6D6D70]">{fmtWhen(r.activity_date)}</span>
      </div>
      <p className="mt-1 text-[11px] leading-relaxed">
        <Meta label="Contact" value={r.contact} />
        <Meta label="Type" value={r.activity_type} />
        <Meta label="Outcome" value={r.outcome} />
        <Meta label="Completed by" value={r.completed_by} />
        {(r.next_action || r.next_action_due) && (
          <Meta label="Next action" value={`${r.next_action || "Follow-up"}${r.next_action_due ? ` (${fmtWhen(r.next_action_due)})` : ""}`} />
        )}
      </p>
    </RowShell>
  ));
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`rounded-sm px-2.5 py-1 text-xs font-bold transition-colors ${active ? "bg-[#002248] text-white" : "bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7]"}`}>
      {children}
    </button>
  );
}

export function DrilldownDrawer({ drill, days, onClose }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("current"); // pipeline only
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const isPipeline = drill.type === "pipeline";
  const section = isPipeline
    ? (tab === "current" ? "pipeline_stage" : "pipeline_movement")
    : drill.type === "seven_touch" ? "seven_touch" : "outcome";
  const key = isPipeline ? drill.stageId : drill.key;

  useEffect(() => {
    let live = true;
    setData(null); setFailed(false);
    api.get("/dashboard/drilldown", { params: { section, key, days } })
      .then(({ data: d }) => { if (live) setData(d); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [section, key, days]);

  const periodNote = section === "pipeline_movement" || section === "outcome"
    ? ` · last ${days} days` : "";

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
        data-testid="drilldown-drawer">
        <SheetHeader className="border-b border-[#E4E4E7] px-4 py-3 text-left">
          <SheetTitle className="font-heading text-lg font-bold text-[#002248]" data-testid="drilldown-title">
            {drill.title}
          </SheetTitle>
          <SheetDescription className="text-xs" data-testid="drilldown-subtitle">
            {data ? `${data.total} record(s)` : "Loading"}{periodNote} — click an organization or opportunity to open its record.
          </SheetDescription>
          {isPipeline && (
            <div className="flex gap-1.5 pt-1">
              <TabBtn active={tab === "current"} onClick={() => setTab("current")} testId="drilldown-tab-current">
                In This Stage
              </TabBtn>
              <TabBtn active={tab === "movements"} onClick={() => setTab("movements")} testId="drilldown-tab-movements">
                Stage Movements
              </TabBtn>
            </div>
          )}
          {drill.type === "outcome" && drill.key === "No outcome recorded" && (
            <button type="button" onClick={() => { onClose(); navigate("/activities?missing_outcome=1"); }}
              className="w-fit pt-1 text-xs font-bold text-[#2E86D6] hover:underline" data-testid="drilldown-fix-outcomes">
              Open in Activities to correct these records
            </button>
          )}
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {failed ? (
            <p className="px-4 py-8 text-center text-xs text-[#DC2626]" data-testid="drilldown-error">
              These records could not be loaded right now. Close and try again.
            </p>
          ) : !data ? (
            <div className="flex justify-center py-10"><CircleNotch size={22} className="animate-spin text-[#002248]" /></div>
          ) : data.rows.length === 0 ? (
            <p className="px-4 py-8 text-center text-xs text-[#6D6D70]" data-testid="drilldown-empty">
              No records available in this environment for this category{periodNote ? " and period" : ""}.
            </p>
          ) : section === "pipeline_stage" ? <StageRows rows={data.rows} />
            : section === "pipeline_movement" ? <MovementRows rows={data.rows} />
              : section === "seven_touch" ? <SevenTouchRows rows={data.rows} />
                : <OutcomeRows rows={data.rows} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
